/**
 * Utility to parse www-authenticate header from API responses
 *
 * Header format:
 * - www-authenticate: Bearer realm="Secure API", error="<error_type>"
 *
 * Error types:
 * - no_token: Token is missing (403)
 * - invalid_token: Token is invalid (403)
 * - expired_token: Token has expired (403)
 * - needs_authentication: User needs to authenticate (403)
 * - token_exchange: Proactive refresh signal on successful (200) responses at ~30min mark
 */

const WWW_AUTHENTICATE_ERRORS = {
    NO_TOKEN: 'no_token',
    INVALID_TOKEN: 'invalid_token',
    EXPIRED_TOKEN: 'expired_token',
    NEEDS_AUTHENTICATION: 'needs_authentication',
    TOKEN_EXCHANGE: 'token_exchange'
};

/**
 * Parse www-authenticate header value
 * @param {string} headerValue - The www-authenticate header value
 * @returns {Object} Parsed header with error property
 */
function parseWwwAuthenticateHeader(headerValue) {
    if (!headerValue || typeof headerValue !== 'string') {
        return { error: null };
    }

    const result = {
        error: null,
        realm: null
    };

    // Parse error attribute: error="error_type"
    const errorMatch = headerValue.match(/error="([^"]+)"/);

    if (errorMatch) {
        result.error = errorMatch[1];
    }

    // Parse realm attribute: realm="Secure API"
    const realmMatch = headerValue.match(/realm="([^"]+)"/);

    if (realmMatch) {
        result.realm = realmMatch[1];
    }

    return result;
}

/**
 * Get www-authenticate header from response (handles different response types)
 * @param {Response|Object} response - Fetch Response object or object with headers property
 * @returns {string|null} The www-authenticate header value or null
 */
function getWwwAuthenticateHeader(response) {
    if (!response) {
        return null;
    }

    // Handle fetch Response object
    if (response.headers && typeof response.headers.get === 'function') {
        return response.headers.get('www-authenticate');
    }

    // Handle plain object with headers
    if (response.headers && typeof response.headers === 'object') {
        // Try both lowercase and case-sensitive variations
        return response.headers['www-authenticate'] || response.headers['WWW-Authenticate'] || response.headers['Www-Authenticate'];
    }

    return null;
}

export default {
    WWW_AUTHENTICATE_ERRORS,
    parseWwwAuthenticateHeader,
    getWwwAuthenticateHeader
};
